<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('salary_management.edit_receipt') }}
            </h6>
      
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2">
        <div class="p-4">
          <LoadingSpinner v-if="loading" :message="$t('common.loading')" class="py-4" />
          <template v-else>
            <form @submit.prevent="submit" @keydown.enter.prevent>
              <div class="row g-3">

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ $t('salary_management.receipt_no') }}</label>
                  <input type="text" class="form-control borderless-input" :value="receipt.receipt_no || '—'" disabled />
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ $t('salary_management.employee') }}</label>
                  <input type="text" class="form-control borderless-input" :value="receipt.model_name || '—'" disabled />
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ $t('salary_management.type') }} *</label>
                  <select
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.type }"
                    v-model="form.type"
                    required
                  >
                    <option value="salary">{{ $t('salary_management.salary') }}</option>
                    <option value="on_account">{{ $t('salary_management.on_account') }}</option>
                    <option value="reward">{{ $t('salary_management.reward') }}</option>
                    <option value="deduction">{{ $t('salary_management.deduction') }}</option>
                  </select>
                  <div class="invalid-feedback">{{ errors.type }}</div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ $t('salary_management.value') }} *</label>
                  <input
                    type="number"
                    step="0.01"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.value }"
                    v-model.number="form.value"
                    required
                  />
                  <div class="invalid-feedback">{{ errors.value }}</div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ $t('salary_management.payment_date') }} *</label>
                  <input
                    type="date"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.payment_date }"
                    v-model="form.payment_date"
                    required
                  />
                  <div class="invalid-feedback">{{ errors.payment_date }}</div>
                </div>

                <div class="col-12">
                  <label class="bmd-label-float">{{ $t('salary_management.description') }}</label>
                  <textarea
                    class="form-control borderless-input"
                    rows="3"
                    :placeholder="$t('salary_management.payment_description_placeholder')"
                    v-model.trim="form.description"
                  ></textarea>
                </div>
              </div>
            </form>

            <div class="d-flex gap-3 mt-4">
              <button
                type="button"
                class="btn btn-primary btn-sm flex-grow-1"
                @click="submit"
                :disabled="submitting"
              >
                <span v-if="submitting"><i class="fas fa-spinner fa-spin me-2"></i>{{ $t('common.saving') }}</span>
                <span v-else><i class="fas fa-save me-2"></i>{{ $t('common.save') }}</span>
              </button>

              <button
                type="button"
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="goBack"
                :disabled="submitting"
              >
                <i class="fas fa-times me-2"></i>{{ $t('common.cancel') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import api from '@/api';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();

const id = route.params.id;

const loading = ref(true);
const submitting = ref(false);
const receipt = ref({
  id: null,
  receipt_no: '',
  type: '',
  value: 0,
  payment_date: '',
  description: '',
  model_name: '',
});

const form = ref({
  type: '',
  value: null,
  payment_date: '',
  description: ''
});

const errors = ref({});

// --- helpers to prevent NaN ---
const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const cleaned = String(v).replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const loadReceipt = async () => {
  loading.value = true;
  try {
    const { data } = await api.get(`/salary-management/receipts/${id}`);
    const r = data?.data ?? data;

    receipt.value = {
      id: r.id,
      receipt_no: r.receipt_no || 'N/A',
      type: r.type || 'salary',
      value: toNumber(r.value ?? r.amount ?? 0),
      payment_date: r.payment_date || new Date().toISOString().split('T')[0],
      description: r.description || '',
      model_name: r.model_name || r.employee_name || r.user_name || '—'
    };

    form.value = {
      type: receipt.value.type,
      value: receipt.value.value,
      payment_date: receipt.value.payment_date,
      description: receipt.value.description
    };
  } catch (e) {
    toast.error(e?.response?.data?.message || t('common.error'));
  } finally {
    loading.value = false;
  }
};

const validate = () => {
  errors.value = {};
  if (!form.value.type) errors.value.type = t('validation.required');
  if (!form.value.value || toNumber(form.value.value) <= 0)
    errors.value.value = t('validation.positive_number');
  if (!form.value.payment_date) errors.value.payment_date = t('validation.required');
  return Object.keys(errors.value).length === 0;
};

const submit = async () => {
  if (!validate()) return;
  submitting.value = true;

  try {
    const payload = {
      type: form.value.type,                     // salary | on_account | reward
      value: toNumber(form.value.value),        // always numeric
      payment_date: form.value.payment_date,
      description: form.value.description
    };

    // Use Vuex action if available; fallback to direct API
    if (store._actions['salaryManagement/updateReceipt']) {
      await store.dispatch('salaryManagement/updateReceipt', { id: receipt.value.id, data: payload });
    } else {
      await api.put(`/salary-management/receipts/${receipt.value.id}`, payload);
    }

    toast.success(t('common.saved'));
    router.push({ name: listRouteForType(form.value.type) });
  } catch (e) {
    toast.error(e?.response?.data?.message || t('common.error'));
  } finally {
    submitting.value = false;
  }
};

// Rewards/deductions now have their own dedicated list pages — return to
// whichever one matches the receipt's type instead of always dropping back
// into the combined "إدارة الرواتب" list. Salary/on_account receipts (no
// dedicated list) still fall back to the combined list.
const listRouteForType = (type) => {
  if (type === 'reward') return 'salary.rewardsList';
  if (type === 'deduction') return 'salary.deductionsList';
  return 'salary.management';
};

const goBack = () => router.push({ name: listRouteForType(receipt.value.type) });

onMounted(loadReceipt);
</script>

<style scoped>
.borderless-input { border: none; border-bottom: 1px solid #e9ecef; border-radius: 0; }
.borderless-input:focus { box-shadow: none; border-bottom-color: #6c757d; }
</style>
